import { Component, EventEmitter, Output, Input, HostListener, ElementRef, OnInit } from '@angular/core';
import { FilterImportResults, FilterSectionsToUpdate } from '../models/filter-import.models';
import { ChanceBaseTierType, Tier, UniqueTierType } from '../models/item-data-models';
import { RulePreviewComponent } from '../common/rule-preview/rule-preview.component';
import { DisplayOptions, Filter, PreviewCustomRule } from '../models/filter-models';
import { flattenAllRulesInGroupsByGroupOrder, totalRulesInCustomGroups } from '../common/custom-rules/custom-rule-groups-helper';
import { Rarity } from "../models/enums";
import { CurrencyTierService } from '../services/tiering/currency-tier.service';
import { UniquesTierService } from '../services/tiering/uniques-tier.service';
import { ChanceBasesTierService } from '../services/tiering/chance-bases-tier.service';
import { BaseComponent } from '../common/base.component';

@Component({
  selector: 'app-import-filter-dialog',
  imports: [RulePreviewComponent],
  templateUrl: './import-filter-dialog.component.html',
  styleUrls: ['../common/dialogs.scss', './import-filter-dialog.component.scss']
})
export class ImportFilterDialogComponent extends BaseComponent implements OnInit {
  @Input({ required: true }) filterResults!: FilterImportResults;
  @Input() sectionsToUpdate: FilterSectionsToUpdate = new FilterSectionsToUpdate();
  @Input() clone = false;
  @Input() apply = false;

  @Output() close = new EventEmitter<void>();
  @Output() updateFilter = new EventEmitter<FilterSectionsToUpdate>();

  Tier = Tier;

  tierCosmeticPreviewRules: PreviewCustomRule[] = [];

  allCustomTiers = false;
  myEquipmentAndFlasks = false;

  constructor(private elementRef: ElementRef, private currencyTierService: CurrencyTierService,
    private uniquesTierService: UniquesTierService, private chanceBasesTierService: ChanceBasesTierService) {
    super();
  }

  ngOnInit(): void {
    if (!this.filterResults.filter) return;

    this.tierCosmeticPreviewRules.push(...this.filterResults.filter.tierCosmetics.map(c => new PreviewCustomRule({
      exampleName: this.currencyTierService.getFirstCurrencyForDropTier(c.tier!),
      displayOptions: c.displayOptions,
      itemClass: 'Stackable Currency',
    })) || []);

    this.tierCosmeticPreviewRules.push(this.createCosmetics(this.getFirstUniqueForUniqueTier('Excellent'), false, Rarity.Unique, this.filterResults.filter.uniqueTierCosmetics[0]!.displayOptions));
    this.tierCosmeticPreviewRules.push(this.createCosmetics(this.getFirstUniqueForUniqueTier('Good'), false, Rarity.Unique, this.filterResults.filter.uniqueTierCosmetics[1]!.displayOptions));
    this.tierCosmeticPreviewRules.push(this.createCosmetics(this.getFirstUniqueForUniqueTier('Potential'), false, Rarity.Unique, this.filterResults.filter.uniqueTierCosmetics[2]!.displayOptions));
    this.tierCosmeticPreviewRules.push(this.createCosmetics(this.getFirstUniqueForUniqueTier('Other'), false, Rarity.Unique, this.filterResults.filter.uniqueTierCosmetics[3]!.displayOptions));

    this.tierCosmeticPreviewRules.push(this.createCosmetics('Stellar Amulet', false, Rarity.Normal, this.filterResults.filter.chanceBaseTierCosmetics[0]!.displayOptions));
    this.tierCosmeticPreviewRules.push(this.createCosmetics(this.getFirstChanceBaseForChanceBaseTier('Potential'), false, Rarity.Normal, this.filterResults.filter.chanceBaseTierCosmetics[1].displayOptions));

    this.tierCosmeticPreviewRules.forEach((rule: PreviewCustomRule, index: number) => {
      rule.order = index;
    });

    this.allCustomTiers = this.sectionsToUpdate.customCurrencyAndDivCardTiers;
  }

  private createCosmetics(exampleName: string, isCurrency: boolean, rarity: Rarity, displayOptions: DisplayOptions) {
    const rule = new PreviewCustomRule({
      displayOptions,
      itemClass: isCurrency ? 'Stackable Currency' : undefined,
      exampleName,
      rarity,
    });

    return rule;
  }

  getFirstUniqueForUniqueTier(tier: UniqueTierType) {
    return this.uniquesTierService.getTieredUniques()[tier][0]?.baseType ?? "Unique";
  }

  getFirstChanceBaseForChanceBaseTier(tier: ChanceBaseTierType) {
    return this.chanceBasesTierService.getTieredChanceBases()[tier][0]?.baseType ?? "Chance Base";
  }

  cosmeticImportRuleCount(f: Filter | null | undefined): number {
    return totalRulesInCustomGroups(f?.customCosmeticRuleGroups);
  }

  cosmeticImportRulesPreview(f: Filter | null | undefined) {
    return f ? flattenAllRulesInGroupsByGroupOrder(f.customCosmeticRuleGroups) : [];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const dialogElement = this.elementRef.nativeElement.querySelector('.dialog');
    if (dialogElement && !dialogElement.contains(event.target)) {
      this.closeDialog();
    }
  }

  closeDialog() {
    this.close.emit();
  }

  importSections() {
    this.updateFilter.emit(this.sectionsToUpdate);
  }

  toggleAllCustomTiers() {
    this.allCustomTiers = !this.allCustomTiers;
    this.sectionsToUpdate.customCurrencyAndDivCardTiers = this.allCustomTiers;
    this.sectionsToUpdate.customUniqueTiers = this.allCustomTiers;
    this.sectionsToUpdate.customChanceBaseTiers = this.allCustomTiers;
    this.sectionsToUpdate.customGearTiers = this.allCustomTiers;
    this.sectionsToUpdate.customJewelleryTiers = this.allCustomTiers;
    this.sectionsToUpdate.customEquipmentTiers = this.allCustomTiers;
    this.sectionsToUpdate.customModTiers = this.allCustomTiers;
    this.sectionsToUpdate.tieringModeCustom = this.allCustomTiers;
    this.sectionsToUpdate.uniquesTieringModeCustom = this.allCustomTiers;
  }

  toggleAllMyEquipmentAndFlasks() {
    this.myEquipmentAndFlasks = !this.myEquipmentAndFlasks;
    this.sectionsToUpdate.myWeapons = this.myEquipmentAndFlasks;
    this.sectionsToUpdate.myArmour = this.myEquipmentAndFlasks;
    this.sectionsToUpdate.myJewellery = this.myEquipmentAndFlasks;
    this.sectionsToUpdate.myJewel = this.myEquipmentAndFlasks;
    this.sectionsToUpdate.myFlasks = this.myEquipmentAndFlasks;
  }
}
